import { ORDER_STATUSES, type OrderStatusKey } from '@c214/shared';
import { useState } from 'react';
import type { AdminOrder } from '../../hooks/useAdminOrders';
import { useUpdateOrderStatus } from '../../hooks/useAdminOrders';
import { OrderStatusDot } from '../orders/OrderStatusDot';
import { Button } from '../ui/button';
import { Chip } from '../ui/chip';
import { Input } from '../ui/input';

// Зміни статусу/ТТН не застосовуються по кліку на чіп — лише після явного
// «Зберегти» в режимі редагування. Це й захищає від випадкових змін, тому
// бекенд дозволяє будь-який перехід між статусами.
export function OrderStatusEditor({ order }: { order: AdminOrder }) {
  const [editing, setEditing] = useState(false);
  const [draftStatus, setDraftStatus] = useState<OrderStatusKey>(order.status);
  const [draftTtn, setDraftTtn] = useState(order.ttn ?? '');
  const updateStatus = useUpdateOrderStatus();

  const startEditing = () => {
    setDraftStatus(order.status);
    setDraftTtn(order.ttn ?? '');
    updateStatus.reset();
    setEditing(true);
  };

  const hasChanges = draftStatus !== order.status || draftTtn.trim() !== (order.ttn ?? '');

  const save = () => {
    updateStatus.mutate(
      { id: order.id, status: draftStatus, ttn: draftTtn.trim() || null },
      { onSuccess: () => setEditing(false) },
    );
  };

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-4">
        <OrderStatusDot status={order.status} />
        <Button variant="secondary" size="sm" onClick={startEditing}>
          ✎ РЕДАГУВАТИ
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 border border-line-soft p-4">
      <div className="flex flex-wrap gap-2">
        {ORDER_STATUSES.map((option) => (
          <Chip
            key={option.key}
            active={draftStatus === option.key}
            onClick={() => setDraftStatus(option.key)}
          >
            {option.label}
          </Chip>
        ))}
      </div>

      <Input
        placeholder="ТТН Нової Пошти (обовʼязково для «В дорозі»)"
        value={draftTtn}
        onChange={(e) => setDraftTtn(e.target.value)}
        className="max-w-[320px]"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="accent"
          size="sm"
          disabled={!hasChanges || updateStatus.isPending}
          onClick={save}
        >
          {updateStatus.isPending ? 'ЗБЕРЕЖЕННЯ…' : 'ЗБЕРЕГТИ'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={updateStatus.isPending}
          onClick={() => setEditing(false)}
        >
          СКАСУВАТИ
        </Button>
      </div>

      {updateStatus.error && <p className="text-[12px] text-warn">{updateStatus.error.message}</p>}
    </div>
  );
}
